import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { news } from "@/mocks/news";
import type { NewsCategory } from "@/types";
import Breadcrumb from "@/components/feature/Breadcrumb";

const CATEGORIES: (NewsCategory | "全部")[] = ["全部", "赛况", "战报", "花边", "交易", "数据"];

export default function News() {
  const [category, setCategory] = useState<(typeof CATEGORIES)[number]>("全部");

  const list = useMemo(
    () => (category === "全部" ? news : news.filter((n) => n.category === category)),
    [category],
  );

  const [featured, ...rest] = list;

  return (
    <div className="px-4 md:px-8 py-6 md:py-8">
      <Breadcrumb items={[{ label: "新闻资讯" }]} />

      <header className="mt-4">
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">NBA新闻资讯</h1>
        <p className="mt-1.5 text-sm text-foreground-500">最新NBA赛事动态、战报、数据与花边资讯</p>
      </header>

      <div className="mt-5 flex flex-wrap gap-2">
        {CATEGORIES.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => setCategory(c)}
            className={`rounded-full px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
              category === c ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-600 hover:bg-background-200"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      {list.length === 0 ? (
        <div className="mt-8 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
          <i className="ri-newspaper-line text-4xl text-foreground-300" />
          <p className="mt-3 text-sm font-medium text-foreground-700">该分类下暂无资讯</p>
          <button
            type="button"
            onClick={() => setCategory("全部")}
            className="mt-4 inline-flex items-center gap-1 rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 whitespace-nowrap transition-colors hover:bg-primary-600"
          >
            <i className="ri-refresh-line" /> 查看全部资讯
          </button>
        </div>
      ) : (
        <>
          {featured && (
            <Link
              to={`/news/${featured.id}`}
              className="group mt-6 grid grid-cols-1 gap-4 overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300 md:grid-cols-2"
            >
              <div className="h-56 w-full overflow-hidden bg-background-200 md:h-full">
                <img src={featured.image} alt={featured.title} title={`${featured.title} - NBA直播资讯`} className="h-full w-full object-top" />
              </div>
              <div className="flex flex-col justify-center p-5 md:p-8">
                <span className="w-fit rounded bg-accent-100 px-2 py-0.5 text-[11px] font-semibold text-accent-800">{featured.category}</span>
                <h2 className="mt-3 font-heading text-xl font-bold text-foreground-950 transition-colors group-hover:text-primary-600 md:text-2xl">
                  {featured.title}
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">{featured.excerpt}</p>
                <p className="mt-4 text-xs text-foreground-500">{featured.author} · {featured.date}</p>
              </div>
            </Link>
          )}

          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {rest.map((article) => (
              <Link
                key={article.id}
                to={`/news/${article.id}`}
                className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
              >
                <div className="h-44 w-full overflow-hidden bg-background-200">
                  <img src={article.image} alt={article.title} title={`${article.title} - NBA直播资讯`} className="h-full w-full object-top" />
                </div>
                <div className="flex flex-1 flex-col p-4">
                  <span className="w-fit rounded bg-secondary-100 px-2 py-0.5 text-[11px] font-semibold text-secondary-900">{article.category}</span>
                  <h3 className="mt-2 line-clamp-2 font-heading text-base font-bold text-foreground-950 transition-colors group-hover:text-primary-600">
                    {article.title}
                  </h3>
                  <p className="mt-2 line-clamp-2 text-sm text-foreground-600">{article.excerpt}</p>
                  <p className="mt-auto pt-3 text-xs text-foreground-500">{article.date}</p>
                </div>
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
}